import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Users,
  BookOpen,
  FolderOpen,
  StickyNote,
  Hash,
  Settings,
  Fingerprint,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Cpu,
  Eye,
} from 'lucide-react'
import { useAuthStore } from '@personic/shared'

const NAV_ITEMS = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    to: '/dashboard',
  },
  { type: 'divider', label: 'My Context' },
  {
    label: 'Overview',
    icon: Eye,
    to: '/identity/context/overview',
  },
  {
    label: 'People',
    icon: Users,
    to: '/identity/context/people',
  },
  {
    label: 'Life Context',
    icon: BookOpen,
    to: '/identity/context/life',
  },
  {
    label: 'Projects',
    icon: FolderOpen,
    to: '/identity/context/projects',
  },
  {
    label: 'Notes',
    icon: StickyNote,
    to: '/identity/context/notes',
  },
  {
    label: 'Recent Topics',
    icon: Hash,
    to: '/identity/context/topics',
  },
  {
    label: 'Settings',
    icon: Settings,
    to: '/identity/context/settings',
  },
  {
    label: 'Profile',
    icon: Fingerprint,
    to: '/identity/context/profile',
  },
]

function NavItem({
  item,
  collapsed,
}: {
  item: (typeof NAV_ITEMS)[number]
  collapsed: boolean
}) {
  if ('type' in item && item.type === 'divider') {
    return (
      <div className="mt-4 mb-1">
        {!collapsed && (
          <p className="px-3 text-xs font-semibold uppercase tracking-widest text-indigo-400/60">
            {item.label}
          </p>
        )}
        {collapsed && <div className="mx-3 border-t border-indigo-800/40 my-2" />}
      </div>
    )
  }

  const Icon = (item as { icon: typeof LayoutDashboard; to: string; label: string }).icon
  const to = (item as { to: string }).to
  const label = (item as { label: string }).label

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150',
          isActive
            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/40'
            : 'text-indigo-200 hover:bg-indigo-800/40 hover:text-white',
          collapsed ? 'justify-center' : '',
        ].join(' ')
      }
      title={collapsed ? label : undefined}
    >
      <Icon size={18} className="shrink-0" />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  )
}

export default function ConsumerLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const { user, logout } = useAuthStore()

  const handleLogout = async () => {
    await logout()
    window.location.href = 'https://personicai.com/auth/signin'
  }

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?'

  return (
    <div className="flex h-screen bg-[#fffbf5] overflow-hidden">
      {/* Sidebar */}
      <motion.aside
        animate={{ width: collapsed ? 64 : 240 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="relative flex flex-col bg-[#1e1b4b] border-r border-indigo-900/40 shrink-0 overflow-hidden"
      >
        {/* Logo */}
        <div
          className={[
            'flex items-center gap-2 px-4 py-5 border-b border-indigo-900/40',
            collapsed ? 'justify-center' : '',
          ].join(' ')}
        >
          <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
            <Cpu size={16} className="text-white" />
          </div>
          <AnimatePresence>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.15 }}
                className="flex flex-col leading-tight"
              >
                <span className="text-white font-bold text-sm tracking-tight">Personic AI</span>
                <span className="text-indigo-400 text-xs font-medium tracking-wide">Identity</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item, i) => (
            <NavItem key={i} item={item} collapsed={collapsed} />
          ))}
        </nav>

        {/* User + Logout */}
        <div className="border-t border-indigo-900/40 px-2 py-3 space-y-1">
          <div
            className={[
              'flex items-center gap-3 px-3 py-2',
              collapsed ? 'justify-center' : '',
            ].join(' ')}
          >
            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
              {initials}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name || 'User'}</p>
                <p className="text-indigo-400 text-xs truncate">{user?.email || ''}</p>
              </div>
            )}
          </div>

          <button
            onClick={handleLogout}
            className={[
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-indigo-300 hover:bg-indigo-800/40 hover:text-white transition-all',
              collapsed ? 'justify-center' : '',
            ].join(' ')}
            title={collapsed ? 'Sign out' : undefined}
          >
            <LogOut size={16} className="shrink-0" />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute top-5 -right-3 w-6 h-6 rounded-full bg-indigo-700 border border-indigo-600 flex items-center justify-center text-indigo-200 hover:text-white hover:bg-indigo-600 transition-all shadow-lg"
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </motion.aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  )
}
